import React from 'react';

// Shared chrome for the two per-bill PSACC statements (standard + stripping).
// Both documents are the same letterhead, addressed-to block, signature block
// and print CSS around a different table, so that frame lives here once. The
// date-range PSACC print pages are deliberately left alone — they group many
// bills and have their own layout.

export const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

// Defaults for the printed letterhead, matching the standalone PSACC print
// pages verbatim (PSACCBillingPrintPage / PSACCBillingStripPrintPage). Real
// Store data still wins whenever it is populated — these only fill the gap.
//
// Note for whoever reads this next: a blank Store is no longer visible on the
// printout, so if these defaults ever go stale they will print silently. The
// controller logs a warning naming exactly which of companyName / address /
// tin is missing (resolveTruckerLetterhead in truckingReportsController.js) —
// check the server log, not the paper, to tell whether a Store is configured.
// The trucker's registered name on these statements. Hardcoded, not read from
// the Store: this is the legal entity the SOA is issued under, and the Store
// record's companyName is free text that has drifted from it. Change it here.
const TRUCKER_COMPANY_NAME = 'ARVANNAH RENZ TRUCKING INC.';

// The carrier these statements are addressed to. Hardcoded for the same reason
// as the trucker name above: bill.CarrierName is free text and holds the
// abbreviation ("PSACC") on existing bills, but the statement must carry the
// carrier's registered name. The ADDRESS still comes from the bill, which is
// correct there.
const CARRIER_COMPANY_NAME = 'PHILIPPINE SPAN ASIA CARRIER CORP.';

const FALLBACK = {
  address: 'Q. ABETO ST. BRGY. ABETO-MIRASOL TAFT SOUTH MANDURIAO, ILOILO CITY',
  tin: 'TIN NO. 768-950-988-000-VAT',
  carrierName: 'PHILIPPINE SPAN ASIA CARRIER CORP.',
  carrierAddress: 'Coastal Road, Brgy. Balabago, Jaro, Iloilo City',
  // No cfoName field exists on Store or Tenant — the signatory is supplied by
  // the template, exactly as every standalone PSACC print page does.
  cfoName: 'JOHN EFREN OCTAVIANO',
};

export const PRINT_CSS = `
  body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print { .no-print { display: none !important; } }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  th { background: #f0f0f0; border: 1px solid #999; padding: 3px 6px; text-align: left; font-size: 8.5pt; }
  td { border: 1px solid #999; padding: 3px 6px; }
  .company-header { text-align: center; border: none !important; padding: 2px 0 6px !important; }
  .company-header .name { font-weight: 700; font-size: 11.5pt; }
  .company-header .line { font-size: 8.5pt; }
  .summary-block { display: flex; justify-content: flex-end; margin-top: 16px; }
  .summary-block table { width: auto; }
  .summary-block td { border: none; padding: 1px 8px; }
  .sig-block { display: flex; justify-content: space-between; font-size: 9.5pt; margin-top: 40px; }
  .totals-rule td { border-top: 2px solid #000; padding-top: 4px; font-weight: 700; }
  .merged-total { text-align: center; vertical-align: middle; font-weight: 600; }
  .spacer-row td { border: none; padding: 3px 0; }
  /* The two statements are one print job but separate documents — the strip
     statement always starts on its own sheet. */
  .doc-break { page-break-before: always; break-before: page; }
`;

// The letterhead lives inside <thead> so it repeats on every printed page —
// same trick the date-range PSACC print pages use.
export function PrintLetterhead({ colSpan, trucker, carrier, statementNo, statementLabel }) {
  return (
    <>
      <tr>
        <td colSpan={colSpan} className="company-header">
          <div className="name">{TRUCKER_COMPANY_NAME}</div>
          <div className="line">{trucker?.address || FALLBACK.address}</div>
          <div className="line">{trucker?.tin ? `TIN NO. ${trucker.tin}` : FALLBACK.tin}</div>
          <div className="line" style={{ fontWeight: 600, marginTop: 2 }}>
            {statementLabel || 'STATEMENT OF ACCOUNT NO.'} {statementNo || '___________'}
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={colSpan} className="company-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', marginBottom: 8 }}>
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontWeight: 600, margin: 0 }}>{CARRIER_COMPANY_NAME}</p>
              <p style={{ margin: 0 }}>{carrier?.address || FALLBACK.carrierAddress}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0 }}>Date: <strong>{fmtDate(new Date())}</strong></p>
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

export function PrintSignatures({ cfo }) {
  return (
    <div className="sig-block">
      <div>
        <p style={{ fontWeight: 600, borderTop: '1px solid #000', paddingTop: 4, paddingRight: 64, margin: 0 }}>
          {cfo?.name || FALLBACK.cfoName}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '9pt', color: '#555' }}>CHIEF FINANCIAL OFFICER</p>
      </div>
      <div>
        <p style={{ margin: '0 0 16px' }}>
          RECEIVED BY: <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span>
        </p>
        <p style={{ margin: 0 }}>
          Date: <span style={{ display: 'inline-block', borderBottom: '1px solid #000', width: 180 }}>&nbsp;</span>
        </p>
      </div>
    </div>
  );
}

export function SummaryRows({ rows }) {
  return (
    <div className="summary-block">
      <table>
        <tbody>
          {rows.map(({ label, value, rule }) => (
            <tr key={label} className={rule ? 'totals-rule' : undefined}>
              <td style={{ textAlign: 'right', color: rule ? '#000' : '#555', fontWeight: rule ? 700 : 400 }}>{label}</td>
              <td style={{ textAlign: 'right', fontWeight: rule ? 700 : 600 }}>₱{PHP(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Groups flat per-bill lines by Vessel / Voyage in order of first appearance,
// so a per-bill statement reproduces the span layout the date-range PSACC
// reports print — same grouping TruckingBillingPrintPage does.
export function groupBySpan(lines, amountOf) {
  const groups = [];
  const index = new Map();
  for (const r of lines) {
    const key = r.vesselVoyage || '—';
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, rows: [], subtotal: 0 });
    }
    const g = groups[index.get(key)];
    g.rows.push(r);
    g.subtotal += amountOf(r);
  }
  return groups;
}

export function PrintShell({ children }) {
  return (
    <>
      <style>{PRINT_CSS}</style>
      <div className="page">{children}</div>
      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
